import React, { Fragment, useCallback, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

import cx from 'classnames'
import styles from './Topbar.module.scss'

import UserBlank from './user-blank.svg'
import Dropdown, { DropdownItem, useDropdownContext } from 'Design/components/Dropdown'
import Authorized from '../../components/Authorized'
import PromptModal from 'Design/components/PromptModal'

import meApi, { useLogoutMutation, useGetMyDataQuery, useAccountsByUserEmailMutation } from 'Services/me'
import botProfilesApi from 'Services/bot-profiles'
import accountApi, { useSwitchAccountMutation } from 'Services/account'
import { useSelector, useDispatch } from 'react-redux'

import { $getAccountDetails } from 'Selectors/account'

const UserDropdown = ({ onLogoutClick, userAccountOptions, selectedAccount }) => {
  const navigate = useNavigate()
  const { data: user } = useGetMyDataQuery()

  const [showSwitchAccountOptions, setShowSwitchAccountOptions] = useState(false)

  const [switchAccount] = useSwitchAccountMutation()

  const dispatch = useDispatch()

  const handleAccountChange = (account) => {
    const promise = switchAccount({ id: account.value, email: user.email }).unwrap()

    toast.promise(promise, {
      loading: 'Switching Account',
      success: 'Account Switched Successfully',
      error: 'Error while switching account.'
    })

    promise.then(
      (res) => {
        if (res) {
          dispatch(meApi.util.resetApiState())
          dispatch(accountApi.util.resetApiState())
          dispatch(botProfilesApi.util.resetApiState())

          let url = `${window.location.origin}`
          if (res?.new_ui) {
            url = `${url}/b/dashboard`
          } else {
            url = `${url}/dashboard`
          }
          window.location.replace(url)
        }
      },
      (err) => {
        console.error('Unable to switch account', err)
        toast.error('Error while switching account.')
      }
    )
  }

  const handleAccountManageClick = () => {
    navigate('accounts')
  }

  return (
    <Fragment>
      <DropdownItem>{user.name}</DropdownItem>
      <Authorized permissions={['user_read', 'account_settings_modify']} requires='some'>
        <DropdownItem onClick={handleAccountManageClick} onMouseOver={() => setShowSwitchAccountOptions(false)}>
          Manage Account
        </DropdownItem>
      </Authorized>
      <DropdownItem
        className={cx({ [styles.HighlightSwitchAccount]: showSwitchAccountOptions })}
        onMouseOver={() => setShowSwitchAccountOptions(true)}
      >
        <div>Switch Account</div>
        <div
          className={cx(showSwitchAccountOptions ? styles.ShowSwitchAccountOptions : styles.HideSwitchAccountOptions)}
        >
          <Fragment>
            {userAccountOptions &&
              userAccountOptions.length &&
              userAccountOptions.map((option) => {
                return (
                  <DropdownItem
                    key={option.value}
                    className={cx({ [styles.HighlightSelectedAccount]: selectedAccount?.value === option.value })}
                    onClick={() => handleAccountChange(option)}
                  >
                    {option.label}
                  </DropdownItem>
                )
              })}
          </Fragment>
        </div>
      </DropdownItem>
      <DropdownItem onClick={onLogoutClick} onMouseOver={() => setShowSwitchAccountOptions(false)}>
        Logout
      </DropdownItem>
    </Fragment>
  )
}

const Action = () => {
  const { getReferenceProps } = useDropdownContext()

  return (
    <div {...getReferenceProps()}>
      <UserBlank height='25' width='25' />
    </div>
  )
}

const UserActions = () => {
  const navigate = useNavigate()
  const { data: user } = useGetMyDataQuery()

  const [logout, logoutResult] = useLogoutMutation()
  const [getAccountsByUserEmail] = useAccountsByUserEmailMutation()

  const [showLogoutConfirmModal, setShowLogoutConfirmModal] = useState(false)
  const [userAccountOptions, setUserAccountOptions] = useState([])
  const [selectedAccount, setSelectedAccount] = useState(null)

  const { id: accountId } = useSelector($getAccountDetails)

  const dispatch = useDispatch()

  useEffect(() => {
    const promise = getAccountsByUserEmail(user.email).unwrap()

    promise.then(
      (data) => {
        if (data && data.length > 0) {
          const options = data.map((item) => {
            return {
              label: item.company_name,
              value: item.accountId
            }
          })
          const selectedAccount = options.find((option) => {
            return option.value === accountId
          })
          if (selectedAccount) {
            setSelectedAccount(selectedAccount)
          }
          setUserAccountOptions(options)
        }
      },
      (err) => {
        console.error('Unable to get user accounts', err)
      }
    )
  }, [accountId, getAccountsByUserEmail, user.email])

  const handleLogout = useCallback(async () => {
    try {
      await logout().unwrap()
      localStorage.removeItem('auth')
      navigate('/login')
      localStorage.removeItem('skip_onboarding')
      localStorage.removeItem('get_started')
      setTimeout(() => {
        dispatch(meApi.util.invalidateTags(['Me', 'CsrfToken']))
      })
    } catch (err) {
      console.error('Unable to logout', err)
      toast.error('Error while logging out.')
    }
  }, [navigate, logout, dispatch])

  const handleOpenModal = () => {
    setShowLogoutConfirmModal(true)
  }

  const handleCloseModal = () => {
    setShowLogoutConfirmModal(false)
  }

  const isLoading = logoutResult.isLoading
  return (
    <Fragment>
      <PromptModal
        isOpen={showLogoutConfirmModal}
        title='Confirm Logout'
        message='Are you sure to logout?'
        onCancel={handleCloseModal}
        onProceed={handleLogout}
        isConfirmButtonDisabled={isLoading}
      />
      <Dropdown
        id='user-action-root'
        offset={15}
        action={<Action />}
        dropdown={
          <UserDropdown
            onLogoutClick={handleOpenModal}
            userAccountOptions={userAccountOptions}
            selectedAccount={selectedAccount}
          />
        }
      />
    </Fragment>
  )
}

export default UserActions
